/**
 * ContactFormStudentFields renders the student fields for the contact form.
 *
 * At time of writing, this component must be controlled.  Either with the
 * useContactFormStudentFields custom hook or with some kind of custom logic.
 * Because angela genuinely doesn't want to try to figure out how to support
 * an uncontrolled version of this component.
 */

import DynamicListField from "@/app/_components/forms/DynamicListField";
import Input from "@/app/_components/forms/Input";
import TextField, {TextFieldClasses} from "@/app/_components/forms/TextField";
import clsMerge from "@/app/util/clsMerge";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlusCircle} from "@fortawesome/sharp-regular-svg-icons/faPlusCircle";
import {StudentsByApp} from "@/app/types/Student";
import CheckboxGroup from "@/app/_components/forms/CheckboxGroup";
import Checkbox from "@/app/_components/forms/Checkbox";
import StudentName, {StudentNameClasses} from "./AcademyStudentName";
import InputWithCheckboxToggle from "./InputWithCheckboxToggle";
import {
	ContactFormSelectedStudents,
	ContactFormStudentHandlers,
	validateNoStudentNameDuplicates,
} from "./contactFormUtils";
import {useEffect, useState} from "react";
import {faCircleExclamation} from "@fortawesome/free-solid-svg-icons/faCircleExclamation";
import {nameMaxLength} from "@/app/util/formUtils";

export type NewStudentFieldBaseProps = {
	classes?: {
		addStudentButton?: string;
		fields?: TextFieldClasses;
		studentNames?: StudentNameClasses;
	};
	allStudents?: StudentsByApp;
};

type NewStudentFieldProps = ContactFormSelectedStudents &
	ContactFormStudentHandlers &
	NewStudentFieldBaseProps;

const NewStudentFields: React.FC<NewStudentFieldProps> = ({
	allStudents,
	classes,
	initialStudentName,
	newStudentNames,
	selectedAcademyStudents,
	selectedPlatformStudents,
	platformStudentNames,
	onInitialStudentChange,
	setNewStudent,
	setSelectedAcademyStudents,
	setSelectedPlatformStudents,
	onPlatformStudentNameChange,
}) => {
	const {academyStudents, platformStudents} = allStudents ?? {};
	const [duplicateError, setDuplicateError] = useState<string | undefined>();

	// Validate for duplicates across all student name fields
	useEffect(() => {
		const hasNoDuplicates = validateNoStudentNameDuplicates({
			initialStudentName,
			newStudentNames,
			selectedPlatformStudents,
			platformStudents,
			platformStudentNames,
			selectedAcademyStudents,
			academyStudents,
		});
		if (hasNoDuplicates) {
			setDuplicateError(undefined);
		} else {
			setDuplicateError("Please remove duplicate student names");
		}
	}, [
		initialStudentName,
		newStudentNames,
		selectedPlatformStudents,
		platformStudents,
		platformStudentNames,
		selectedAcademyStudents,
		academyStudents,
	]);

	return (
		<>
			{!academyStudents?.length && !platformStudents?.length && (
				<TextField
					isRequired
					name="newStudentNames"
					labelText="Student First Name"
					type="text"
					autoComplete="section-student given-name"
					value={initialStudentName}
					onInput={onInitialStudentChange}
					classes={classes?.fields}
					maxLength={nameMaxLength}
				>
					<Input />
				</TextField>
			)}
			{!!academyStudents?.length && (
				<CheckboxGroup
					labelText="Request consultation for:"
					hideLabelDecorators
					classes={{root: "flex flex-col gap-2"}}
					name="existingStudentIds"
					value={selectedAcademyStudents}
					onChange={setSelectedAcademyStudents}
				>
					{academyStudents.map((student) => {
						return (
							<Checkbox value={student.userId} key={student.userId}>
								<StudentName {...student} classes={classes?.studentNames} />
							</Checkbox>
						);
					})}
				</CheckboxGroup>
			)}
			{!!platformStudents?.length && (
				<CheckboxGroup
					classes={{root: "flex flex-col gap-2 max-w-96 w-96"}}
					name="uninitializedStudents"
					aria-label="Request consultations for uninitialized students"
					value={selectedPlatformStudents}
					onChange={setSelectedPlatformStudents}
				>
					{platformStudents.map((student) => {
						return (
							<InputWithCheckboxToggle
								checkboxLabel={student.displayName}
								inputProps={{
									"aria-label": `${student.displayName}'s First Name`,
									placeholder: `${student.displayName}'s First Name`,
									name: student.platformId,
									value: platformStudentNames?.[student.platformId] ?? "",
									required: true,
									onChange: onPlatformStudentNameChange,
									maxLength: nameMaxLength,
								}}
								value={student.platformId}
								key={student.platformId}
							/>
						);
					})}
				</CheckboxGroup>
			)}

			<DynamicListField
				fieldName="newStudentNames"
				label={(index) => `Additional Student ${index + 1} First Name`}
				fieldData={newStudentNames}
				onFieldChange={setNewStudent}
				isEachRequired
				classes={{
					addButton: clsMerge(
						"self-start text-hyperlink hover:text-navy-600 hover:no-underline",
						classes?.addStudentButton
					),
					fields: classes?.fields,
				}}
				textFieldProps={{
					maxLength: nameMaxLength,
				}}
			>
				<FontAwesomeIcon icon={faPlusCircle} className="pr-1 pt-4" /> Add
				another student
			</DynamicListField>
			{duplicateError && (
				<div className="flex flex-row items-start gap-2">
					<FontAwesomeIcon
						className="flex-none pt-1 text-semantic-error-300"
						icon={faCircleExclamation}
					/>
					<div className="pt-0.5 text-sm text-semantic-error-500">
						{duplicateError}
					</div>
				</div>
			)}
		</>
	);
};

export default NewStudentFields;
